import { useStates } from '@tiger/hooks';
import { useSWR } from '@tiger/swr';
import { Button, Form, Input, Switch } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { $Formutil, withForm } from 'react-antd-formutil';
import { useNavigate } from 'react-router-dom';
import { BankCardItemTypes } from 'components/BankCardSelect';
import BlackListWarning from 'components/BlackListWarning';
import BottomBox from 'components/BottomBox';
import Debug from 'components/Debug';
import ErrorBox from 'components/ErrorBox';
import FieldSelect from 'components/FieldSelect';
import FormItem from 'components/FormItem';
import Loading from 'components/Loading';
import Modal from 'components/Modal';
import SafetyVerification from 'components/SafetyVerification';
import ScrollTop from 'components/ScrollTop';
import Toast from 'components/Toast';
import { useBlackWhiteStatus } from 'hooks/useBlackWhiteStatus';
import { useGetBankCards } from 'modules/BankCards/request';
import TaxYearSelect from 'modules/Withdraw/components/TaxYearSelect';
import { openIRAWithdrawLimitsModal } from 'modules/Withdraw/helper';
import { useDistributionTaxYears } from 'modules/Withdraw/hooks';
import iconTip from 'static/images/icons/icon-tip.svg';
import security from 'stores/security';
import API from 'utils/API';
import { formatMoney } from 'utils/format';
import http from 'utils/http';
import { printf } from 'utils/i18n';
import { useCurrency } from '..';
import Instructions from '../components/Instructions';
import { useCaptchaErrorModal } from '../utils';
import Amount from './Amount';
import BankCard from './BankCard';
import { customErrorMsgMap } from './helper';
import NoCard from './NoCard';
import './style.scss';

const emptyArr = [];

interface DefaultProps {
    $formutil: $Formutil<{
        card_id: number;
        amount: string;
        reason: string;
        tax_rate?: string;
        tax_year?: number;
    }>;
}

export interface DefaultStates {
    balance?: number;
    balanceError: Error | null;
    currentBankInfo?: BankCardItemTypes;
}

const Default: React.FC<DefaultProps> = ({ $formutil }) => {
    const { $invalid, $batchState, $params, $getFirstError } = $formutil;
    const navigate = useNavigate();
    const currency = useCurrency();
    const isIRA = security.isIRAAccount;
    const [showTax, setShowTax] = useState(false);
    const { isLoading: taxYearListDataLoading } = useDistributionTaxYears();
    const [{ balanceError, balance, currentBankInfo }, setState] = useStates<DefaultStates, any>({
        balanceError: null
    });
    const {
        isMutating,
        error,
        data,
        trigger: getBankCardTrigger
    } = useGetBankCards({
        queryType: 'withdraw',
        way: 'WIRE'
    });

    const { data: reasonData = emptyArr } = useSWR<
        Array<{
            reasonDesc: string;
            reasonName: string;
        }>
    >(
        {
            url: API.TRADE.deposit.getWithdrawalReason(),
            params: {
                reason_type: isIRA ? 'ira' : 'individual',
                customer_id: security.currentCustomerId,
                account_id: security.currentAccountId
            }
        },
        {
            dataChain: resp => resp.data
        }
    );

    const { data: blackListData } = useBlackWhiteStatus('WIRE_WITHDRAW');

    useEffect(() => {
        getBankCardTrigger();
    }, [getBankCardTrigger]);

    useEffect(() => {
        if (data?.length) {
            setState({
                currentBankInfo: data?.[0]
            });
        }
    }, [data, setState]);

    const openCaptchaErrorModal = useCaptchaErrorModal();

    const getBalance = useCallback(
        async bankAccount => {
            Toast.loading(true);

            setState({
                balanceError: null
            });

            try {
                const balance = await http
                    .get<{
                        withdrawable: number;
                    }>(API.TRADE.withdrawableCash(), {
                        params: {
                            account_id: security.currentAccountId,
                            customer_id: security.currentCustomerId,
                            ddaAccountNum: bankAccount
                        }
                    })
                    .then(({ data }) => data?.withdrawable);

                setState({
                    balance
                });
            } catch (error) {
                setState({
                    balanceError: error
                });
            }

            Toast.loading(false);
        },
        [setState]
    );

    useEffect(() => {
        // 切换银行卡时需要重新获取可出金金额
        if (currentBankInfo) {
            getBalance(currentBankInfo.bankAccount);
        }
    }, [currentBankInfo, getBalance]);

    const handleSubmit = () => {
        if ($invalid) {
            $batchState({
                $dirty: true,
                $touched: true
            });

            const errorText = $getFirstError();

            Toast.show(customErrorMsgMap[errorText] || errorText);
        } else {
            const { amount, reason, tax_year } = $params;
            const { id, holderName } = currentBankInfo!;

            Modal.open({
                wrapClassName: 'wire-safety-verify-modal',
                component: props => (
                    <SafetyVerification
                        {...props}
                        process="personal_withdraw"
                        errorHandle={error => openCaptchaErrorModal(error)}
                        tradePasswordEnabled={security.userInfo?.tradePassword}
                        onSubmit={async ({ verify_code, type }) => {
                            Toast.loading(true);

                            const params: Record<string, string | number | undefined> = {
                                ...(type === 'sms'
                                    ? {
                                          verify_code
                                      }
                                    : {}),
                                process: 'personal_withdraw',
                                way: 'WIRE',
                                amount,
                                currency,
                                card_id: id,
                                reason,
                                holder_name: holderName,
                                customer_id: security.currentCustomerId,
                                account_id: security.currentAccountId,
                                external_id: Date.now()
                            };

                            if (isIRA) {
                                params.tax_rate = showTax ? Number($params.tax_rate) / 100 : 0;
                                params.tax_year = tax_year;
                            }

                            try {
                                await http.post(API.TRADE.wire.submitWireWithdraw(), params, {
                                    useForm: true,
                                    tradeAuth: type === 'tradePassword'
                                });

                                navigate('complete', {
                                    state: {
                                        // 手动拼写完成页地址 不会进入
                                        successKey: Date.now()
                                    }
                                });
                            } catch (err) {
                                Toast.show(err);
                            }

                            Toast.loading(false);
                        }}
                    />
                ),
                closable: true,
                width: 400
            }).result.catch(() => {});
        }
    };

    const renderFormInfo = () => {
        if (balanceError) {
            return (
                <ErrorBox
                    error={balanceError}
                    onClick={() => {
                        if (currentBankInfo) {
                            getBalance(currentBankInfo.bankAccount);
                        }
                    }}
                />
            );
        }

        return (
            <>
                <Amount balance={balance} currency={currency} isIRA={isIRA} />
                {balance !== undefined && (
                    <p className="balance">
                        {printf(
                            isIRA ? __('%s is available to distribute') : __('%s is available to withdraw'),
                            `${formatMoney(balance, '$')}`
                        )}
                    </p>
                )}
                {isIRA && (
                    <div className="tax-label" style={{ paddingBottom: 8 }}>
                        <div>{__('Withholding tax')}</div>
                        <Switch checked={showTax} onChange={setShowTax} />
                    </div>
                )}
                {isIRA && showTax && (
                    <FormItem
                        name="tax_rate"
                        className="tax-item"
                        $parser={(value, setValue) => {
                            const num = parseInt(value, 10);
                            const intValue = isNaN(num) ? '' : String(num);

                            setValue(intValue);

                            return intValue;
                        }}
                        $onFieldChange={() => {
                            $formutil.$validate('amount');
                        }}
                        checker={value => {
                            if (typeof value !== 'string' || value.length === 0) {
                                return __('Please enter 10-100, or turn off the switch');
                            }

                            if (value.indexOf('.') !== -1) {
                                return __('Please enter an integer');
                            }

                            if (Number(value) < 10 || Number(value) > 100) {
                                return __('Please enter 10-100, or turn off the switch');
                            }

                            return true;
                        }}>
                        <Input placeholder={__('Please enter 10-100')} suffix="%" />
                    </FormItem>
                )}
                <FormItem
                    label={isIRA ? __('Distribution Reason') : __('Withdrawal Reason')}
                    name="reason"
                    checker={value => {
                        if (!value) {
                            return isIRA
                                ? __('Please select distribution reason')
                                : __('Please select withdrawal reason');
                        }

                        return true;
                    }}>
                    <FieldSelect
                        placeholder={
                            isIRA ? __('Please select distribution reason') : __('Please select withdrawal reason')
                        }
                        data={reasonData.map(item => ({ value: item.reasonDesc, label: item.reasonDesc }))}
                    />
                </FormItem>
                <Instructions />
                <Debug data={$params} />
                <BottomBox className="btn-box">
                    <Button className="btn-submit btn-important" block type="primary" htmlType="submit">
                        {__('Continue')}
                    </Button>
                </BottomBox>
            </>
        );
    };

    const render = () => {
        if (isMutating || taxYearListDataLoading) {
            return <Loading />;
        }

        if (error && !data) {
            return <ErrorBox error={error} onClick={() => getBankCardTrigger()} />;
        }

        if (blackListData?.isBlack) {
            return <BlackListWarning source="withdraw" type="Wire" />;
        }

        if (!data?.length) {
            return <NoCard />;
        }

        return (
            <Form layout="vertical" onFinish={handleSubmit}>
                <TaxYearSelect name="tax_year" />
                <BankCard
                    list={data}
                    currentBankInfo={currentBankInfo}
                    onChange={cardId => {
                        setState({
                            currentBankInfo: data.find(({ id }) => id === cardId)
                        });
                    }}
                />
                {renderFormInfo()}
            </Form>
        );
    };

    return (
        <div className="withdraw-wire-default-root">
            <ScrollTop />
            <h3 className="main-title">
                {isIRA ? __('Wire Distribution') : __('Withdraw by wire')}
                {isIRA && <img src={iconTip} alt="tip" onClick={() => openIRAWithdrawLimitsModal()} />}
            </h3>
            {render()}
        </div>
    );
};

export default withForm(Default);
